import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { NorthLoadingProgress } from "./NorthLoadingProgress";
import { useNavigate, Link } from "react-router-dom";
import { useTeamUsage } from "@/hooks/useTeamUsage";
import { ZillowIcon } from "@/components/icons/ZillowIcon";
import { RealtorIcon } from "@/components/icons/RealtorIcon";
import { UpgradeDialog } from "./UpgradeDialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertTriangle, Compass } from "lucide-react";
import { logger } from "@/lib/logger";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { useTeamAgents } from "@/hooks/useTeamAgents";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";

const zillowFormSchema = z.object({
  zillowUrl: z.string().url("Must be a valid URL").refine(
    (url) => url.includes("zillow.com"),
    "Must be a valid Zillow URL"
  ),
  realtorUrl: z.string().url("Must be a valid URL").optional().or(z.literal("")).refine(
    (url) => !url || url.includes("realtor.com"),
    "Must be a valid Realtor.com URL"
  ),
  sellerNames: z.string().optional(),
  sellerEmail: z.string().email("Must be a valid email").optional().or(z.literal("")),
  listingAgentName: z.string().optional(),
});

interface CreateNorthDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateNorthDialog({ open, onOpenChange }: CreateNorthDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [createdNorthId, setCreatedNorthId] = useState<string | null>(null);
  const [initialTimestamps, setInitialTimestamps] = useState<{
    executive_summary_updated_at?: string | null;
    comps_summary_updated_at?: string | null;
    neighborhood_summary_updated_at?: string | null;
  } | undefined>(undefined);
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState<string>("");
  const { toast } = useToast();
  const navigate = useNavigate();
  const { getEffectiveUserId, isImpersonating } = useImpersonation();
  const { checkLimit, incrementUsage, showUpgradePrompt, upgradeDialogOpen, setUpgradeDialogOpen, upgradeFeature, subscription, planLimits, usage, getUsagePercentage } = useTeamUsage();
  const { agents } = useTeamAgents();

  const zillowForm = useForm<z.infer<typeof zillowFormSchema>>({
    resolver: zodResolver(zillowFormSchema),
    defaultValues: {
      zillowUrl: "",
      realtorUrl: "",
      sellerNames: "",
      sellerEmail: "",
      listingAgentName: "",
    },
  });

  // Check admin status and fetch user's profile to default Listing Agent Name
  useEffect(() => {
    const checkAdminAndFetchProfile = async () => {
      if (open) {
        // Reset form when dialog opens
        zillowForm.reset({
          zillowUrl: "",
          realtorUrl: "",
          sellerNames: "",
          sellerEmail: "",
          listingAgentName: "",
        });
        setSelectedAgentId("");

        try {
          const effectiveUserId = await getEffectiveUserId();
          if (!effectiveUserId) return;

          // Check if user is admin
          const { data: roleData } = await supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', effectiveUserId)
            .eq('role', 'admin')
            .maybeSingle();

          setIsAdmin(!!roleData);

          // If not admin, set default name from profile
          if (!roleData) {
            const { data: profile } = await supabase
              .from('profiles')
              .select('full_name')
              .eq('user_id', effectiveUserId)
              .maybeSingle();

            if (profile?.full_name) {
              zillowForm.setValue('listingAgentName', profile.full_name);
            }
          }
        } catch (error) {
          logger.error('Error checking admin status or fetching profile:', error);
        }
      }
    };

    checkAdminAndFetchProfile();
  }, [open, zillowForm, getEffectiveUserId]);

  // Handle agent selection from dropdown
  useEffect(() => {
    if (selectedAgentId && isAdmin) {
      const selectedAgent = agents.find(a => a.id === selectedAgentId);
      if (selectedAgent) {
        zillowForm.setValue('listingAgentName', selectedAgent.name);
        // Note: headshot_url will be set in the onZillowSubmit function
      }
    }
  }, [selectedAgentId, agents, isAdmin, zillowForm]);

  const onZillowSubmit = async (values: z.infer<typeof zillowFormSchema>) => {
    // Check if user has reached their plan limit
    if (!checkLimit('norths')) {
      showUpgradePrompt('North Reports');
      return;
    }

    // Show loading state immediately for faster UX
    setIsLoading(true);
    
    // Declare variables outside try block so they're accessible in catch
    let effectiveUserId: string | null = null;
    let profile: any = null;
    
    try {
      effectiveUserId = await getEffectiveUserId();
      if (!effectiveUserId) {
        toast({
          title: "Error",
          description: "You must be logged in to create a North",
          variant: "destructive",
        });
        return;
      }

      // Get user's profile to populate "Created By", company logo, and brand colors
      // Also verify team_id exists (required for RLS policy)
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('full_name, company_logo_url, default_primary_color, default_secondary_color, email, team_id')
        .eq('user_id', effectiveUserId)
        .maybeSingle();

      if (profileError) {
        throw new Error(`Failed to load profile: ${profileError.message}`);
      }

      if (!profileData) {
        throw new Error("Profile not found. Please refresh the page and try again.");
      }

      profile = profileData;

      // Verify team_id exists (required by RLS policy for inserts)
      if (!profile.team_id) {
        throw new Error("Your account setup is incomplete. Please refresh the page and try again. If the issue persists, contact support.");
      }

      const createdByName = profile.full_name || profile.email || 'Unknown';
      const companyLogoUrl = profile.company_logo_url || null;
      const primaryColor = profile.default_primary_color || '#0c0f24';
      const secondaryColor = profile.default_secondary_color || '#1b489b';
      
      // Use form values for custom_agent_name and seller_names, fallback to defaults
      // If admin selected from roster, get agent info
      let listingAgentName = values.listingAgentName || createdByName;
      let customAgentHeadshotUrl: string | null = null;

      if (isAdmin && selectedAgentId) {
        const selectedAgent = agents.find(a => a.id === selectedAgentId);
        if (selectedAgent) {
          listingAgentName = selectedAgent.name;
          customAgentHeadshotUrl = selectedAgent.headshot_url;
        }
      }
      const sellerNames = values.sellerNames || null;
      const sellerEmail = values.sellerEmail?.trim() || null;

      const { data: scrapedData, error: scrapeError } = await supabase.functions.invoke(
        "scrape-zillow",
        {
          body: { zillowUrl: values.zillowUrl },
        }
      );

      if (scrapeError) throw scrapeError;

      // Parse lot_size to extract numeric value
      let parsedLotSize = null;
      if (scrapedData.lotSize) {
        // Remove commas, "sqft", and any other text, then convert to number
        const numericValue = scrapedData.lotSize.toString().replace(/[,\s]/g, '').match(/[\d.]+/);
        if (numericValue) {
          parsedLotSize = parseFloat(numericValue[0]);
        }
      }

      // Fetch Realtor.com metrics if URL is provided
      let realtorViews = 0;
      let realtorSaves = 0;
      
      // If Realtor.com URL is provided, use it directly
      if (values.realtorUrl && values.realtorUrl.trim()) {
        try {
          logger.log('Fetching Realtor.com metrics using provided URL:', values.realtorUrl);
          const { data: realtorData, error: realtorError } = await supabase.functions.invoke(
            "fetch-realtor-metrics",
            {
              body: { realtorUrl: values.realtorUrl.trim() },
            }
          );

          if (!realtorError && realtorData) {
            realtorViews = realtorData.views || 0;
            realtorSaves = realtorData.saves || 0;
            logger.log('Realtor.com metrics fetched:', { 
              views: realtorViews, 
              saves: realtorSaves,
              url: values.realtorUrl
            });
          } else {
            logger.log('Realtor.com metrics fetch failed or skipped:', { 
              error: realtorError,
              url: values.realtorUrl
            });
          }
        } catch (realtorErr) {
          // Silently skip if Realtor.com fetch fails
          logger.log('Realtor.com metrics fetch error (skipping):', { 
            error: realtorErr,
            url: values.realtorUrl
          });
        }
      } else {
        logger.log('No Realtor.com URL provided, skipping Realtor.com metrics');
      }

      // Log the insert attempt for debugging
      logger.log('Attempting to insert north:', {
        user_id: effectiveUserId,
        hasProfile: !!profile,
        hasTeamId: !!profile?.team_id,
        address: scrapedData.address
      });

      const { data, error } = await supabase
        .from("norths")
        .insert([{
          user_id: effectiveUserId,
          zillow_url: values.zillowUrl,
          realtor_url: values.realtorUrl?.trim() || null,
          zpid: scrapedData.zpid,
          mls_id: scrapedData.mlsId,
          address: scrapedData.address,
          city: scrapedData.city,
          state: scrapedData.state,
          zip: scrapedData.zip,
          property_type: scrapedData.propertyType,
          bedrooms: scrapedData.bedrooms,
          bathrooms: scrapedData.bathrooms,
          square_feet: scrapedData.squareFeet,
          lot_size: parsedLotSize ? parsedLotSize.toString() : null,
          year_built: scrapedData.yearBuilt,
          price: scrapedData.price,
          image_url: scrapedData.imageUrl,
          description: scrapedData.description,
          features: scrapedData.features,
          listing_agent: scrapedData.listingAgent || 'Unknown',
          listing_agent_photo: scrapedData.listingAgentPhoto,
          custom_agent_name: listingAgentName,
          custom_agent_headshot_url: customAgentHeadshotUrl,
          created_by: createdByName,
          seller_names: sellerNames,
          seller_email: sellerEmail,
          additional_images: scrapedData.additionalImages || [],
          days_on_zillow: scrapedData.daysOnZillow,
          views_count: scrapedData.zillowViews || 0,
          saves_count: scrapedData.zillowSaves || 0,
          realtor_views: realtorViews,
          realtor_saves: realtorSaves,
          showings_count: 0,
          price_changes_count: 0,
          price_history: scrapedData.priceHistory || [],
          show_social_on_report: false,
          show_showings_on_report: false,
          is_draft: false,
          is_processing: true, // Hide from list until all data is fetched
        }])
        .select()
        .single();

      if (error) throw error;

      // Create processing jobs for background execution with rate limiting
      if (data?.id && values.zillowUrl) {
        logger.log('Creating processing jobs for new north');

        // Get user's profile to fetch team_id
        const { data: profile } = await supabase
          .from('profiles')
          .select('team_id')
          .eq('user_id', effectiveUserId)
          .maybeSingle();

        const teamId = profile?.team_id || null;

        const jobs = [
          // Priority 10: Fetch comps first (most important for the report)
          {
            north_id: data.id,
            user_id: effectiveUserId,
            team_id: teamId,
            job_type: 'fetch_comps',
            priority: 10,
            config: { zillowUrl: values.zillowUrl }
          },
          // Priority 9: Generate comps summary after comps are fetched
          {
            north_id: data.id,
            user_id: effectiveUserId,
            team_id: teamId,
            job_type: 'generate_comps_summary',
            priority: 9,
            config: {}
          },
          // Priority 8: Fetch floorplan/tour data
          {
            north_id: data.id,
            user_id: effectiveUserId,
            team_id: teamId,
            job_type: 'fetch_floorplan_tour',
            priority: 8,
            config: { zpid: scrapedData.zpid }
          },
          // Priority 7: Fetch neighborhood data (using ZIP code for broader area results)
          {
            north_id: data.id,
            user_id: effectiveUserId,
            team_id: teamId,
            job_type: 'fetch_neighborhood_data',
            priority: 7,
            config: {
              zip: scrapedData.zip
            }
          },
          // Priority 6: Generate neighborhood summary
          {
            north_id: data.id,
            user_id: effectiveUserId,
            team_id: teamId,
            job_type: 'generate_neighborhood_summary',
            priority: 6,
            config: {}
          },
          // Priority 5: Generate executive summary (runs last, needs comps + neighborhood summaries)
          {
            north_id: data.id,
            user_id: effectiveUserId,
            team_id: teamId,
            job_type: 'generate_executive_summary',
            priority: 5,
            config: { summaryType: 'executive' }
          }
        ];

        // Note: sync_social_media removed from queue - runs on-demand in Social Media Manager

        try {
          const { error: jobsError } = await supabase
            .from('north_processing_jobs')
            .insert(jobs);

          if (jobsError) {
            logger.error('Error creating processing jobs:', jobsError);
            logger.error('Job creation error details:', jobsError);
            toast({
              title: "Warning",
              description: `Report created but background processing may be delayed: ${jobsError.message}`,
              variant: "destructive",
            });
          } else {
            logger.log(`Created ${jobs.length} processing jobs for north ${data.id}`);
          }
        } catch (jobsErr) {
          logger.error('Failed to create processing jobs:', jobsErr);
          logger.error('Job creation exception:', jobsErr);
          toast({
            title: "Warning",
            description: `Report created but background processing failed: ${jobsErr instanceof Error ? jobsErr.message : 'Unknown error'}`,
            variant: "destructive",
          });
        }

        // Still sync Airtable showings immediately (only for caldwellrg.com users)
        // This is fast and doesn't have rate limits
        if (profile?.email?.endsWith('@caldwellrg.com')) {
          try {
            await supabase.functions.invoke('sync-airtable', {
              body: { northId: data.id }
            });
          } catch (airtableErr) {
            logger.error('Failed to sync showings:', airtableErr);
          }
        }

      }

      // Note: Usage tracking counts published Norths only, so we don't increment here
      // Usage will be reflected when the North is published (is_published = true)

      // Store the North ID to show progress dialog
      // Keep isLoading true so dialog appears
      if (data?.id) {
        setCreatedNorthId(data.id);
        
        // Get initial timestamps before jobs run
        // NorthLoadingProgress detects completion by observing changes from these initial values
        const { data: northData } = await supabase
          .from('norths')
          .select('executive_summary_updated_at, comps_summary_updated_at, neighborhood_summary_updated_at, neighborhood_data_updated_at')
          .eq('id', data.id)
          .single();
        
        setInitialTimestamps(northData || undefined);
      }
    } catch (error) {
      logger.error("Error creating North from Zillow:", error);
      
      // Extract meaningful error message
      let errorMessage = "Failed to create North from Zillow listing";
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'object' && error !== null) {
        // Handle Supabase errors
        if ('message' in error) {
          errorMessage = String(error.message);
        } else if ('error' in error && typeof error.error === 'string') {
          errorMessage = error.error;
        }
      }
      
      // Log full error details for debugging
      logger.error("Full error details:", {
        error,
        errorMessage,
        effectiveUserId: effectiveUserId || 'not set',
        hasProfile: !!profile,
        hasTeamId: !!profile?.team_id
      });
      
      // Check for common RLS/team_id errors
      if (errorMessage.includes('team_id') || errorMessage.includes('row-level security') || errorMessage.includes('policy') || errorMessage.includes('403') || errorMessage.includes('Forbidden')) {
        errorMessage = "Permission denied. Please refresh the page and try again. If the issue persists, contact support.";
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      setIsLoading(false);
    }
  };

  return (
    <>
      {isLoading ? (
        createdNorthId ? (
        <NorthLoadingProgress
          northId={createdNorthId}
          initialTimestamps={initialTimestamps}
          onComplete={() => {
            // Store the northId before clearing state
            const northIdToNavigate = createdNorthId;
            
            setIsLoading(false);
            onOpenChange(false);
            zillowForm.reset();
            setCreatedNorthId(null);
            setInitialTimestamps(undefined);

            // Navigate to the seller report page
            // Use setTimeout to ensure navigation happens after dialog closes
            if (northIdToNavigate) {
              setTimeout(() => {
              navigate(`/seller-report/${northIdToNavigate}`);
              }, 100);
            }

            toast({
              title: "Report Complete",
              description: "Your North Report is ready to view!",
            });
          }}
          onDismiss={() => {
            setIsLoading(false);
            onOpenChange(false);
            zillowForm.reset();

            // Only navigate if we have a valid northId
            // Use setTimeout to ensure navigation happens after dialog closes
            if (createdNorthId) {
              setTimeout(() => {
            navigate(`/seller-report/${createdNorthId}`);
              }, 100);
            }
            setCreatedNorthId(null);
            setInitialTimestamps(undefined);
          }}
        />
        ) : (
          <Dialog open={true}>
            <DialogContent className="sm:max-w-[500px] gap-8 [&>button]:hidden">
              <div className="flex flex-col items-center space-y-6 py-6">
                <div className="relative">
                  <Compass className="h-16 w-16 text-primary animate-spin" style={{ animationDuration: "3s" }} />
                </div>
                <div className="space-y-2 text-center">
                  <h2 className="text-2xl font-bold text-foreground">Creating Report</h2>
                  <p className="text-sm text-muted-foreground">Setting up your North Report...</p>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )
      ) : (
        <Dialog open={open} onOpenChange={(newOpen) => {
          // If dialog is being closed and we have a created north, navigate to it
          if (!newOpen && createdNorthId && !isLoading) {
            setTimeout(() => {
              navigate(`/seller-report/${createdNorthId}`);
            }, 100);
          }
          onOpenChange(newOpen);
        }}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="text-2xl">Create New North</DialogTitle>
          </DialogHeader>

          {/* Usage Warning */}
          {planLimits && usage && planLimits.max_active_norths !== null && getUsagePercentage('norths') >= 80 && (
            <Alert variant={getUsagePercentage('norths') >= 100 ? "destructive" : "default"}>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>
                {getUsagePercentage('norths') >= 100 ? 'Usage Limit Reached' : 'Approaching Usage Limit'}
              </AlertTitle>
              <AlertDescription>
                {getUsagePercentage('norths') >= 100 ? (
                  <>
                    You've reached your plan's limit of {planLimits.max_active_norths} active North Reports.{' '}
                    <Link to="/settings?tab=billing" className="underline font-medium" onClick={() => onOpenChange(false)}>
                      Upgrade your plan
                    </Link>{' '}
                    to create more reports.
                  </>
                ) : (
                  <>
                    You have {usage.norths_created} of {planLimits.max_active_norths} active North Reports ({Math.round(getUsagePercentage('norths'))}%).{' '}
                    You're nearing your plan's limit.{' '}
                    <Link to="/settings?tab=billing" className="underline font-medium" onClick={() => onOpenChange(false)}>
                      Upgrade now
                    </Link>{' '}
                    to avoid interruption.
                  </>
                )}
              </AlertDescription>
            </Alert>
          )}

          <Form {...zillowForm}>
            <form onSubmit={zillowForm.handleSubmit(onZillowSubmit)} className="space-y-4">
              <FormField
                control={zillowForm.control}
                name="zillowUrl"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <ZillowIcon className="h-4 w-4" />
                      Zillow URL
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="https://www.zillow.com/homedetails/..." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={zillowForm.control}
                name="realtorUrl"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <RealtorIcon className="h-4 w-4" />
                      Realtor.com URL (Optional)
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="https://www.realtor.com/realestateandhomes-detail/..." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {isAdmin ? (
                <FormItem>
                  <FormLabel>Listing Agent</FormLabel>
                  <Select value={selectedAgentId} onValueChange={setSelectedAgentId}>
                    <SelectTrigger>
                      <SelectValue placeholder={agents.length > 0 ? "Select an agent from roster" : "No agents in roster yet"} />
                    </SelectTrigger>
                    <SelectContent>
                      {agents.length === 0 ? (
                        <div className="px-2 py-1.5 text-sm text-muted-foreground">
                          No agents in roster. Add agents in Settings → Team Roster
                        </div>
                      ) : (
                        agents.map((agent) => (
                          <SelectItem key={agent.id} value={agent.id}>
                            <div className="flex items-center gap-2">
                          <Avatar className="h-6 w-6">
                            <AvatarImage src={agent.headshot_url || undefined} />
                            <AvatarFallback className="text-xs">
                              {agent.name[0]?.toUpperCase() || "A"}
                            </AvatarFallback>
                          </Avatar>
                          <span>{agent.name}</span>
                            </div>
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              ) : (
                <FormField
                  control={zillowForm.control}
                  name="listingAgentName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Listing Agent Name
                      </FormLabel>
                      <FormControl>
                        <Input placeholder="Enter your name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={zillowForm.control}
                name="sellerNames"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Seller Name(s)
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., John & Jane Smith" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={zillowForm.control}
                name="sellerEmail"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Seller Email (Optional)
                    </FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="seller@example.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" variant="gold" className="w-full" disabled={isLoading}>
                Create Report
              </Button>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
      )}

      <UpgradeDialog
        open={upgradeDialogOpen}
        onOpenChange={setUpgradeDialogOpen}
        feature={upgradeFeature}
        currentPlan={subscription?.plan_tier}
      />
    </>
  );
}
